import * as Sentry from "@sentry/react";

// Initialize Sentry only if DSN is available
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    release: process.env.REACT_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false
      }),
      Sentry.replayCanvasIntegration({
        enableManualSnapshot: true,
        maskAllText: false
      }),
      // Add console logging integration
      Sentry.consoleLoggingIntegration({
        levels: ["log", "error", "warn", "info", "debug"]
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample rate for session replay
    replaysOnErrorSampleRate: 1.0, // Sample rate for session replay on errors
    // Enable logging
    _experiments: {
      enableLogs: true,
    },
    // Set user context when available
    beforeBreadcrumb(breadcrumb) {
      return {
        ...breadcrumb,
        timestamp: Date.now() / 1000,
      };
    },
  });
} else {
  console.warn('Sentry DSN not found. Error tracking is disabled.');
}

// Higher-order function to wrap API calls with Sentry monitoring
export const withSentryMonitoring = (apiCall, operation, name) => {
  return async (...args) => {
    return Sentry.startSpan(
      {
        op: `http.client.${operation}`,
        name: name,
      },
      async () => {
        try {
          return await apiCall(...args);
        } catch (error) {
          if (!error?.isAxiosError || !error?.__CANCEL__) {
            Sentry.captureException(error, {
              extra: {
                operation,
                name,
                url: error?.config?.url,
                method: error?.config?.method,
                status: error?.response?.status,
                responseData: error?.response?.data,
                args: args
              }
            });
          }
          throw error;
        }
      }
    );
  };
};

// Higher-order function to wrap component actions with Sentry monitoring
export const withSentryAction = (action, operation, name) => {
  return async (...args) => {
    return Sentry.startSpan(
      {
        op: `ui.action.${operation}`,
        name: name,
      },
      async () => {
        try {
          return await action(...args);
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              operation,
              name,
              component: error?.componentName,
              props: error?.props,
              args: args
            }
          });
          throw error;
        }
      }
    );
  };
};

// Error tracking with enhanced context
export const trackError = (error, context = {}) => {
  const { logger } = Sentry;
  
  // Log the error with additional context
  logger.error(logger.fmt`Error occurred: ${error.message}`, {
    ...context,
    stack: error.stack,
    name: error.name
  });

  Sentry.captureException(error, {
    extra: context,
    tags: {
      errorType: error.name,
      ...context.tags
    }
  });
};

export default Sentry; 